import produce from "immer";
import { create } from "zustand";

import { Order } from "flows/Picking/models/order/types";
import { WOLT_RESELLERS } from "shared/constants";
import { sortByShelf } from "utils/item";

import {
  InputMehod,
  ItemPickingState,
  ItemsMap,
  ItemsPickingState,
  PickingOrigin,
} from "./pickingTypes";
import {
  getActiveItemFromContext,
  getItemPickingStateRef,
  getItemWithPickingState,
  getPickingStateMap,
  selectNextItemInContext,
} from "./pickingUtils";

export type PickingPhase =
  | "idle"
  | "startManualPicking"
  | "pickingStuck"
  | "scanningItems"
  | "scanningContainers"
  | "assigningShelves"
  | "finished";

export type EndPickingPhase = "idle" | "loading" | "error";

const initialItemPickingState: ItemPickingState = {
  state: "IDLE",
  pickedQuantity: 0,
  hasBarcodeIssue: false,
};

export type PickingStoreState = {
  // Flow phase
  pickingPhase: PickingPhase;
  endPickingPhase: EndPickingPhase;

  // Context data
  order: Order | null;
  origin: PickingOrigin | null;
  elapsedTime: number;
  itemsIdsSortedByShelf: string[];
  itemsPickingState: ItemsPickingState;
  pickedGiftsQuantity: number;
  totalGiftsQuantity: number;
  pickedCampaignsQuantity: number;
  totalCampaignsQuantity: number;
  pickedRemoveFlyersQuantity: number;
  totalRemoveFlyersQuantity: number;
  skippedItemsIdsSortedByShelf: string[];
  skippedItemsPickingState: ItemsPickingState;
  itemsMap: ItemsMap;
  containersIds: string[];
  shelvesIds: string[];
  inputMethod: InputMehod | null;

  // Internal timer
  timerInterval: ReturnType<typeof setInterval> | null;
};

type PickingStoreActions = {
  // Phase transitions
  setPickingPhase: (phase: PickingPhase) => void;
  setEndPickingPhase: (phase: EndPickingPhase) => void;

  // Order setup
  setOrder: (order: Order) => void;
  setOrigin: (origin: PickingOrigin) => void;

  // Item actions
  incrementItemQuantity: (
    itemId: string,
    isSkippedItem: boolean,
    reportedBarcodeIssue: boolean,
    pickedByScan?: boolean,
  ) => void;
  decrementItemQuantity: (itemId: string) => void;
  skipItem: (itemId: string, isSkippedItem: boolean) => void;
  resetItem: (itemId: string) => void;

  // Gift / Campaign / Flyers
  incrementGiftQuantity: () => void;
  incrementCampaignQuantity: () => void;
  incrementRemoveFlyersQuantity: () => void;

  // Container / Shelf actions
  addContainer: (containerId: string) => void;
  removeContainer: (containerId: string) => void;
  resetContainers: () => void;
  addShelf: (shelfId: string) => void;
  removeShelf: (shelfId: string) => void;
  resetShelves: () => void;

  // Input method
  setInputMethod: (inputMethod: InputMehod) => void;

  // Timer
  incrementElapsedTime: () => void;
  startTimer: () => void;
  stopTimer: () => void;

  // Reset
  reset: () => void;
};

export type PickingStore = PickingStoreState & PickingStoreActions;

const initialState: PickingStoreState = {
  pickingPhase: "idle",
  endPickingPhase: "idle",
  order: null,
  origin: null,
  elapsedTime: 0,
  itemsIdsSortedByShelf: [],
  itemsPickingState: {},
  pickedGiftsQuantity: 0,
  totalGiftsQuantity: 0,
  pickedCampaignsQuantity: 0,
  totalCampaignsQuantity: 0,
  pickedRemoveFlyersQuantity: 0,
  totalRemoveFlyersQuantity: 0,
  skippedItemsIdsSortedByShelf: [],
  skippedItemsPickingState: {},
  itemsMap: {},
  containersIds: [],
  shelvesIds: [],
  inputMethod: null,
  timerInterval: null,
};

export const usePickingStore = create<PickingStore>()((set, get) => ({
  ...initialState,

  // Phase transitions
  setPickingPhase: (phase) => set({ pickingPhase: phase }),
  setEndPickingPhase: (phase) => set({ endPickingPhase: phase }),

  // Order setup
  setOrder: (order) => {
    if (!order || !order.items) return;

    const itemsIdsSortedByShelf = order.items
      .slice()
      .sort(sortByShelf)
      .map((item) => item.id);

    const { itemsPickingState, itemsMap } = order.items.reduce<{
      itemsPickingState: ItemsPickingState;
      itemsMap: ItemsMap;
    }>(
      (acc, curr) => {
        const isFirstItemInList = itemsIdsSortedByShelf[0] === curr.id;
        return {
          itemsMap: {
            ...acc.itemsMap,
            [curr.id]: curr,
          },
          itemsPickingState: {
            ...acc.itemsPickingState,
            [curr.id]: {
              ...initialItemPickingState,
              state: isFirstItemInList ? "PICKING" : "IDLE",
            },
          },
        };
      },
      { itemsPickingState: {}, itemsMap: {} },
    );

    const isEligibleForGift = order.reseller === "FLINK" && order.isNewCustomer;
    const totalArtificialItem = isEligibleForGift ? 1 : 0;

    const totalRemoveFlyersQuantity = [
      "UBER-EATS",
      "UBER-EATS-CARREFOUR",
      "JUST-EAT",
      ...WOLT_RESELLERS,
    ].includes(order.reseller ?? "")
      ? 1
      : 0;

    set({
      order,
      itemsIdsSortedByShelf,
      itemsPickingState,
      itemsMap,
      totalGiftsQuantity: totalArtificialItem,
      totalCampaignsQuantity: totalArtificialItem,
      totalRemoveFlyersQuantity,
    });
  },

  setOrigin: (origin) => set({ origin }),

  // Item actions
  incrementItemQuantity: (itemId, isSkippedItem, reportedBarcodeIssue) => {
    set(
      produce((state: PickingStoreState) => {
        const selectedItem = getItemWithPickingState(itemId, state, isSkippedItem);
        if (!selectedItem) return;

        const newItemPickedQuantity = selectedItem.pickedQuantity + 1;
        const pickingStateMap = getPickingStateMap(state, isSkippedItem);

        if (newItemPickedQuantity <= selectedItem.item.quantity) {
          pickingStateMap[itemId].pickedQuantity = newItemPickedQuantity;
        }
        if (reportedBarcodeIssue) {
          pickingStateMap[itemId].hasBarcodeIssue = reportedBarcodeIssue;
        }
        if (newItemPickedQuantity === selectedItem.item.quantity) {
          pickingStateMap[itemId].state = "DONE";
          selectNextItemInContext(state);
        }
      }),
    );
  },

  decrementItemQuantity: (itemId) => {
    set(
      produce((state: PickingStoreState) => {
        const isSkippedItem = state.skippedItemsIdsSortedByShelf.includes(itemId);
        const selectedItem = getItemWithPickingState(itemId, state, isSkippedItem);
        if (!selectedItem) return;

        const currentItemPickedQuantity = selectedItem.pickedQuantity;
        if (!currentItemPickedQuantity) return;

        const currentActiveItem =
          getActiveItemFromContext(state, false) || getActiveItemFromContext(state, true);

        if (currentActiveItem && currentActiveItem.item.id !== itemId) {
          const isSkippedActiveItem = currentActiveItem.isSkipped;
          const selectedPickingStateMap = getPickingStateMap(state, isSkippedActiveItem);
          selectedPickingStateMap[currentActiveItem.item.id].state = "IDLE";
        }

        const pickingStateMap = getPickingStateMap(state, isSkippedItem);
        pickingStateMap[itemId].state = "PICKING";
        pickingStateMap[itemId].pickedQuantity = selectedItem.pickedQuantity - 1;
      }),
    );
  },

  skipItem: (itemId, isSkippedItem) => {
    set(
      produce((state: PickingStoreState) => {
        const selectedItemPickingStateRef = getItemPickingStateRef(itemId, state, isSkippedItem);
        if (!selectedItemPickingStateRef) return;

        selectedItemPickingStateRef.state = "DONE";

        if (!isSkippedItem && !state.skippedItemsIdsSortedByShelf.includes(itemId)) {
          state.skippedItemsIdsSortedByShelf.push(itemId);
          state.skippedItemsIdsSortedByShelf.sort(
            (a, b) =>
              state.itemsIdsSortedByShelf.indexOf(a) - state.itemsIdsSortedByShelf.indexOf(b),
          );
          state.skippedItemsPickingState[itemId] = {
            ...initialItemPickingState,
            pickedQuantity: selectedItemPickingStateRef.pickedQuantity,
          };
          state.itemsIdsSortedByShelf = state.itemsIdsSortedByShelf.filter((id) => id !== itemId);
        }

        selectNextItemInContext(state);
      }),
    );
  },

  resetItem: (itemId) => {
    set(
      produce((state: PickingStoreState) => {
        if (state.skippedItemsIdsSortedByShelf.indexOf(itemId) > -1) {
          state.skippedItemsIdsSortedByShelf = state.skippedItemsIdsSortedByShelf.filter(
            (skippedItemId) => skippedItemId !== itemId,
          );
          delete state.skippedItemsPickingState[itemId];
        }

        state.itemsPickingState[itemId] = { ...initialItemPickingState };

        const currentlyActiveItem =
          getActiveItemFromContext(state, false) || getActiveItemFromContext(state, true);

        if (currentlyActiveItem) {
          const pickingStateMap = getPickingStateMap(state, currentlyActiveItem.isSkipped);
          pickingStateMap[currentlyActiveItem.item.id].state = "IDLE";
        }
        selectNextItemInContext(state);
      }),
    );
  },

  // Gift / Campaign / Flyers (with guards)
  incrementGiftQuantity: () => {
    const state = get();
    if (
      state.order?.reseller === "FLINK" &&
      state.order?.isNewCustomer === true &&
      state.pickedGiftsQuantity < state.totalGiftsQuantity
    ) {
      set({ pickedGiftsQuantity: state.pickedGiftsQuantity + 1 });
    }
  },

  incrementCampaignQuantity: () => {
    const state = get();
    if (
      state.order?.reseller === "FLINK" &&
      state.order?.isNewCustomer === true &&
      state.pickedCampaignsQuantity < state.totalCampaignsQuantity
    ) {
      set({ pickedCampaignsQuantity: state.pickedCampaignsQuantity + 1 });
    }
  },

  incrementRemoveFlyersQuantity: () => {
    const state = get();
    if (
      ["UBER-EATS", "UBER-EATS-CARREFOUR", "JUST-EAT", ...WOLT_RESELLERS].includes(
        state.order?.reseller ?? "",
      ) &&
      state.pickedRemoveFlyersQuantity < state.totalRemoveFlyersQuantity
    ) {
      set({ pickedRemoveFlyersQuantity: state.pickedRemoveFlyersQuantity + 1 });
    }
  },

  // Container / Shelf actions
  addContainer: (containerId) => {
    const state = get();
    if (!state.containersIds.includes(containerId)) {
      set({ containersIds: [...state.containersIds, containerId] });
    }
  },

  removeContainer: (containerId) => {
    set({ containersIds: get().containersIds.filter((id) => id !== containerId) });
  },

  resetContainers: () => set({ containersIds: [] }),

  addShelf: (shelfId) => {
    set({ shelvesIds: [...get().shelvesIds, shelfId] });
  },

  removeShelf: (shelfId) => {
    set({ shelvesIds: get().shelvesIds.filter((id) => id !== shelfId) });
  },

  resetShelves: () => set({ shelvesIds: [] }),

  // Input method
  setInputMethod: (inputMethod) => set({ inputMethod }),

  // Timer
  incrementElapsedTime: () => set((state) => ({ elapsedTime: state.elapsedTime + 1 })),

  startTimer: () => {
    const { elapsedTime, timerInterval } = get();
    if (elapsedTime > 0 || timerInterval) return;

    const id = setInterval(() => {
      get().incrementElapsedTime();
    }, 1000);
    set({ timerInterval: id });
  },

  stopTimer: () => {
    const { timerInterval } = get();
    if (timerInterval) {
      clearInterval(timerInterval);
      set({ timerInterval: null });
    }
  },

  // Reset
  reset: () => {
    get().stopTimer();
    set({ ...initialState, timerInterval: null });
  },
}));
